import * as React from 'react'
import { MessageSquare } from 'lucide-react'
import { ticketService, Ticket } from '@/services/ticketService'
import { useToast } from '@/components/ui/use-toast'
import { useWebSocket } from '@/context/WebSocketContext'
import { TicketStats } from '@/components/ticket/TicketStats'
import { TicketFilters } from '@/components/ticket/TicketFilters'
import { TicketTable } from '@/components/ticket/TicketTable'
import { debounce } from '@/lib/debounce'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

type FilterStatus = 'all' | 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed'
type FilterPriority = 'all' | 'low' | 'medium' | 'high' | 'urgent'
type FilterCategory = 'all' | 'bug' | 'feature_request' | 'question' | 'billing' | 'technical' | 'other'

export default function TicketListPage() {
  const { toast } = useToast()
  const ws = useWebSocket()
  const [tickets, setTickets] = React.useState<Ticket[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState<FilterStatus>('all')
  const [filterPriority, setFilterPriority] = React.useState<FilterPriority>('all')
  const [filterCategory, setFilterCategory] = React.useState<FilterCategory>('all')
  const [filterUnread, setFilterUnread] = React.useState(false)

  // Filtrelenmiş ticket listesi
  const filteredTickets = React.useMemo(() => {
    return tickets.filter(ticket => {
      const matchesSearch =
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.ticketNumber.toString().includes(searchQuery) ||
        ticket.createdBy.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.createdBy.email.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus
      const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority
      const matchesCategory = filterCategory === 'all' || ticket.category === filterCategory
      const matchesUnread = !filterUnread || ticket.adminUnread

      return matchesSearch && matchesStatus && matchesPriority && matchesCategory && matchesUnread
    })
  }, [tickets, searchQuery, filterStatus, filterPriority, filterCategory, filterUnread])

  // Debounced arama
  const debouncedSearch = React.useCallback(
    debounce((query: string) => {
      setSearchQuery(query)
    }, 300),
    []
  )

  // Ticketları getir
  const fetchTickets = React.useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await ticketService.getTickets({ limit: 100 })
      setTickets(response.data || [])
    } catch (error) {
      console.error('Ticketlar getirilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Ticketlar getirilirken bir hata oluştu.'
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  React.useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  // WebSocket listeners for real-time updates
  React.useEffect(() => {
    if (!ws) return

    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data)

        // Yeni ticket oluşturuldu
        if (message.type === 'ticket_new') {
          fetchTickets() // Listeyi yenile
          toast({
            title: 'Yeni Destek Talebi',
            description: `#${message.data.ticketNumber} - ${message.data.subject}`
          })
        }

        // Ticket mesajı geldi
        if (message.type === 'ticket_message') {
          fetchTickets() // Listeyi yenile (lastMessageAt güncellemesi için)
        }

        // Ticket durumu değişti
        if (message.type === 'ticket_status') {
          fetchTickets() // Listeyi yenile
        }

        // Ticket silindi
        if (message.type === 'ticket_deleted') {
          fetchTickets() // Listeyi yenile
        }
      } catch (error) {
        console.error('WebSocket mesaj parse hatası:', error)
      }
    }

    ws.addEventListener('message', handleMessage)

    return () => {
      ws.removeEventListener('message', handleMessage)
    }
  }, [ws, toast, fetchTickets])

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gradient-to-r from-emerald-700 to-emerald-500 p-8">
              <div className="flex justify-between items-start relative w-full h-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-4 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-80 bg-white/30" />
                  <Skeleton className="h-16 w-[500px] bg-white/20" />
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10" />
              </div>
            </div>
          </div>

          {/* TicketStats skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 bg-white rounded-xl">
                <CardContent className="p-0">
                  <div className="relative">
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-5 transform translate-x-12 -translate-y-12 rounded-full bg-slate-200 blur-2xl"></div>

                    <div className="p-6 relative">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <Skeleton className="h-4 w-[120px] mb-2" />
                          <Skeleton className="h-9 w-[70px]" />
                        </div>
                        <Skeleton className="h-14 w-14 rounded-xl" />
                      </div>

                      <div className="pt-3 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Skeleton className="w-2 h-2 rounded-full mr-2" />
                            <Skeleton className="h-4 w-[120px]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* TicketFilters skeleton */}
          <Card className="p-6">
            <div className="flex gap-3 items-center">
              <div className="flex-1">
                <Skeleton className="h-11 w-full" />
              </div>
              <Skeleton className="h-11 w-[120px]" />
            </div>
          </Card>

          {/* TicketTable skeleton */}
          <Card>
            <div className="overflow-hidden border border-slate-200 rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[80px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[200px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[150px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[120px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4">
                      <Skeleton className="h-5 w-[100px]" />
                    </TableHead>
                    <TableHead className="py-3.5 px-4 text-right">
                      <Skeleton className="h-5 w-[80px] ml-auto" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i} className="hover:bg-slate-50">
                      <TableCell className="py-4 px-4">
                        <Skeleton className="h-5 w-[60px]" />
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <Skeleton className="h-5 w-[250px]" />
                          <Skeleton className="h-4 w-[180px]" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="flex gap-2">
                          <Skeleton className="h-7 w-[80px] rounded-full" />
                          <Skeleton className="h-7 w-[70px] rounded-full" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <Skeleton className="h-5 w-[120px]" />
                      </TableCell>
                      <TableCell className="py-4 px-4">
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-[90px]" />
                          <Skeleton className="h-4 w-[70px]" />
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-4 text-right">
                        <div className="flex justify-end">
                          <Skeleton className="h-8 w-8 rounded" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #059669, #10b981)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Destek Merkezi
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Müşteri Destek Talepleri
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Müşterilerinizin destek taleplerini görüntüleyin, yanıtlayın ve yönetin.
                  Tüm destek süreçlerini tek bir yerden takip edin ve müşteri memnuniyetini artırın.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
              >
                <MessageSquare className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <TicketStats tickets={tickets} />

        {/* Filtreler */}
        <TicketFilters
          onSearchChange={debouncedSearch}
          onStatusChange={setFilterStatus}
          onPriorityChange={setFilterPriority}
          onCategoryChange={setFilterCategory}
          onUnreadChange={setFilterUnread}
          statusValue={filterStatus}
          priorityValue={filterPriority}
          categoryValue={filterCategory}
          unreadValue={filterUnread}
        />

        {/* Ticket Tablosu */}
        {filteredTickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-lg bg-background">
            <MessageSquare className="h-12 w-12 text-slate-300 mb-3" />
            <p className="text-lg font-medium mb-2">Ticket Bulunamadı</p>
            <p className="text-muted-foreground text-center max-w-md">
              {searchQuery || filterStatus !== 'all' || filterPriority !== 'all' || filterCategory !== 'all' || filterUnread
                ? 'Arama kriterlerinize uygun ticket bulunamadı. Lütfen farklı bir arama yapmayı deneyin.'
                : 'Henüz hiç destek talebi oluşturulmamış.'}
            </p>
          </div>
        ) : (
          <TicketTable tickets={filteredTickets} onTicketDeleted={fetchTickets} />
        )}
      </div>
    </div>
  )
}
